/**
 * Shared element-path utilities for the composition translation projection
 * and its inverse (PLAT-9966).
 *
 * Composition placements are stored as nested `elements` trees in the
 * Creatomate source JSON; translatable controls reference their target
 * source-element by a `number[]` path that's walked one `elements[idx]`
 * level at a time. Both the projection (translate text → renderer payload)
 * and the un-projection (renderer-readback → canonical store) need the
 * same "clone the ancestor chain, rewrite the leaf property, share sibling
 * subtree references" semantics — so the cloning is centralized here.
 */

/**
 * Walk `path` from `root.elements[...]` to a leaf source-element and return
 * a new tree where the leaf's `[property]` has been replaced with `value`.
 * Clones only the ancestor chain leading to the mutated leaf; sibling
 * subtrees retain reference equality with the input, so consumers can use
 * reference equality as a cheap "subtree unchanged" signal.
 *
 * Returns `null` defensively when the path is unresolvable (empty path,
 * non-array `elements`, out-of-bounds index, non-object child).
 *
 * Caller is expected to validate `path.length === 1` against multi-path
 * controls if their policy requires it; this helper accepts any depth.
 */
export function setLeafPropertyByPath(
  root: Record<string, unknown>,
  path: ReadonlyArray<number>,
  property: string,
  value: string,
): Record<string, unknown> | null {
  if (path.length === 0) return null;
  const ancestors: Array<Record<string, unknown>> = [];
  const indices: number[] = [];
  let cursor: Record<string, unknown> = root;
  for (let i = 0; i < path.length; i++) {
    const els = cursor.elements;
    if (!Array.isArray(els)) return null;
    const idx = Number(path[i]);
    if (!Number.isInteger(idx) || idx < 0 || idx >= els.length) return null;
    const child = els[idx];
    if (!child || typeof child !== 'object') return null;
    ancestors.push(cursor);
    indices.push(idx);
    cursor = child as Record<string, unknown>;
  }
  const rewrittenLeaf: Record<string, unknown> = {
    ...cursor,
    [property]: value,
  };
  let replaced: Record<string, unknown> = rewrittenLeaf;
  for (let i = ancestors.length - 1; i >= 0; i--) {
    const parent = ancestors[i];
    const idx = indices[i];
    const parentEls = parent.elements;
    if (!Array.isArray(parentEls)) return null;
    const nextEls = parentEls.slice();
    nextEls[idx] = replaced;
    replaced = { ...parent, elements: nextEls };
  }
  return replaced;
}

/**
 * Read-only version of the same walk: return the leaf node at `path` from
 * `root.elements[...]` without cloning anything. Returns `null` for the
 * same defensive reasons as `setLeafPropertyByPath`.
 */
export function traverseElementPath(
  root: Record<string, unknown>,
  path: ReadonlyArray<number>,
): Record<string, unknown> | null {
  let node: unknown = root;
  for (const segment of path) {
    if (!node || typeof node !== 'object') return null;
    const els = (node as { elements?: unknown }).elements;
    if (!Array.isArray(els)) return null;
    const idx = Number(segment);
    if (!Number.isInteger(idx) || idx < 0 || idx >= els.length) return null;
    node = els[idx];
  }
  if (!node || typeof node !== 'object') return null;
  return node as Record<string, unknown>;
}
